/**
 * Correlação Aderência × Resultado — Matriz de Eficácia do Padrão.
 *
 * Regras anti–falso positivo:
 *  - Só considera indicadores explicitamente vinculados em lst_templates.linked_indicator_id.
 *  - Sem vínculo ou sem meta válida → Nota de Resultado = N/A (nunca 100%).
 *  - Resultado = atingimento Realizado vs. Meta no período (snapshots ou valor atual).
 */

import type { Indicator, IndicatorDirection, IndicatorSnapshot } from '../../../types/indicator';
import type { LstExecucao, LstTemplate } from '../types';
import {
  resolveTemplateIndicator,
  templateHasLinkedIndicator,
  templateIndicatorLabel,
} from '../utils/lstIndicator';

export type EfficiencyQuadrant =
  | 'eficaz'
  | 'padrao_ineficaz'
  | 'processo_instavel'
  | 'critico'
  | 'indisponivel';

export interface IndicatorResultScore {
  /** % de atingimento da meta (0–100) ou null quando incalculável. */
  achievementPct: number | null;
  realized: number | null;
  target: number | null;
  unit: string;
  direction: IndicatorDirection;
  /** Origem do realizado no período. */
  source: 'execution' | 'snapshots' | 'current_value' | 'none';
  detail: string | null;
}

export interface EfficiencyMatrixResult {
  adherencePct: number;
  /** null = N/A — nunca exibir 100% artificial. */
  resultPct: number | null;
  resultLabel: string;
  hasLinkedIndicator: boolean;
  resultAvailable: boolean;
  quadrant: EfficiencyQuadrant;
  title: string;
  message: string;
  tone: 'emerald' | 'amber' | 'red' | 'slate';
  indicatorName: string | null;
  resultScore: IndicatorResultScore | null;
}

const THRESHOLD = 70;

/** Filtra snapshots cujo recorded_at cai no intervalo [start, end] (YYYY-MM-DD). */
export function filterSnapshotsInPeriod(
  snapshots: IndicatorSnapshot[],
  start: string,
  end: string,
): IndicatorSnapshot[] {
  return snapshots.filter((s) => {
    const d = s.recorded_at.slice(0, 10);
    return d >= start && d <= end;
  });
}

/**
 * Atingimento da meta (%) — Realizado vs. Meta.
 *
 * higher_is_better: (realizado / meta) × 100
 * lower_is_better:  (meta / realizado) × 100 — ex: meta 2%, real 4% → 50%
 */
export function computeAchievementPct(
  realized: number,
  target: number,
  direction: IndicatorDirection,
): number {
  if (target <= 0 || !Number.isFinite(realized) || !Number.isFinite(target)) return 0;

  let pct: number;
  if (direction === 'lower_is_better') {
    if (realized <= 0) return 100;
    pct = (target / realized) * 100;
  } else {
    pct = (realized / target) * 100;
  }
  return Math.round(Math.min(100, Math.max(0, pct)));
}

/**
 * Calcula a Nota de Resultado para um indicador no período.
 * Usa média dos snapshots no período; se vazio, cai para current_value.
 */
export function computeIndicatorResultScore(
  indicator: Indicator,
  snapshots: IndicatorSnapshot[],
  periodStart: string,
  periodEnd: string,
): IndicatorResultScore {
  const target = indicator.target_value;
  const unit = indicator.unit ?? '';

  if (target == null || target <= 0) {
    return {
      achievementPct: null,
      realized: indicator.current_value ?? null,
      target: target ?? null,
      unit,
      direction: indicator.direction,
      source: 'none',
      detail: 'Indicador sem meta configurada — impossível calcular atingimento.',
    };
  }

  const inPeriod = filterSnapshotsInPeriod(snapshots, periodStart, periodEnd);
  let realized: number | null = null;
  let source: IndicatorResultScore['source'] = 'none';

  if (inPeriod.length > 0) {
    realized = inPeriod.reduce((s, x) => s + x.value, 0) / inPeriod.length;
    source = 'snapshots';
  } else if (indicator.current_value != null && Number.isFinite(indicator.current_value)) {
    realized = indicator.current_value;
    source = 'current_value';
  }

  if (realized == null) {
    return {
      achievementPct: null,
      realized: null,
      target,
      unit,
      direction: indicator.direction,
      source: 'none',
      detail: 'Sem medições no período selecionado.',
    };
  }

  const achievementPct = computeAchievementPct(realized, target, indicator.direction);
  const detail =
    source === 'snapshots'
      ? `Média de ${inPeriod.length} medição(ões) no período.`
      : 'Valor pontual atual (sem histórico no período).';

  return {
    achievementPct,
    realized,
    target,
    unit,
    direction: indicator.direction,
    source,
    detail,
  };
}

/** Agrega múltiplos indicadores vinculados (média dos atingimentos válidos). */
export function aggregateResultScores(scores: IndicatorResultScore[]): IndicatorResultScore | null {
  const valid = scores.filter((s) => s.achievementPct != null);
  if (valid.length === 0) {
    return scores[0] ?? null;
  }
  const avg =
    valid.reduce((sum, s) => sum + (s.achievementPct as number), 0) / valid.length;
  const first = valid[0];
  return {
    achievementPct: Math.round(avg),
    realized: first.realized,
    target: first.target,
    unit: first.unit,
    direction: first.direction,
    source: first.source,
    detail:
      valid.length > 1
        ? `Média de ${valid.length} indicadores vinculados.`
        : first.detail,
  };
}

/**
 * Retorna APENAS indicadores explicitamente vinculados em lst_templates.
 * Sem fallback para OEE genérico ou primeiro indicador ativo.
 */
export function pickLinkedIndicators(
  templates: Array<{ linked_indicator_id: string | null }>,
  indicators: Indicator[],
): Indicator[] {
  const linkedIds = [
    ...new Set(
      templates.map((t) => t.linked_indicator_id).filter((id): id is string => !!id),
    ),
  ];
  return linkedIds
    .map((id) => indicators.find((i) => i.id === id && i.status === 'active'))
    .filter((i): i is Indicator => !!i);
}

/** Indicador prioritário (primeiro vínculo explícito). */
export function pickLinkedIndicator(
  templates: Array<{ linked_indicator_id: string | null }>,
  indicators: Indicator[],
): Indicator | null {
  return pickLinkedIndicators(templates, indicators)[0] ?? null;
}

export function hasExplicitLinkedIndicator(templates: LstTemplate[]): boolean {
  return templates.some(templateHasLinkedIndicator);
}

/**
 * Nota de Resultado a partir dos valores informados no gemba (lst_execucao.indicator_realized_value).
 * Prioridade sobre snapshots globais — elimina falso positivo de 100%.
 */
export function computeExecutionResultScore(
  templates: LstTemplate[],
  execucoes: LstExecucao[],
  indicators: Indicator[],
  userId: string,
  periodStart: string,
  periodEnd: string,
): IndicatorResultScore | null {
  const linkedTemplates = templates.filter(templateHasLinkedIndicator);
  if (linkedTemplates.length === 0) return null;

  const templateMap = new Map(linkedTemplates.map((t) => [t.id, t]));
  const inPeriod = execucoes.filter(
    (e) =>
      e.user_id === userId &&
      e.data >= periodStart &&
      e.data <= periodEnd &&
      e.indicator_realized_value != null &&
      Number.isFinite(e.indicator_realized_value) &&
      (e.status === 'realizado' || e.status === 'realizado_com_desvio'),
  );

  if (inPeriod.length === 0) return null;

  const pcts: number[] = [];
  let lastMeta: ReturnType<typeof resolveTemplateIndicator> | null = null;

  for (const exec of inPeriod) {
    const tpl = templateMap.get(exec.template_id);
    if (!tpl) continue;
    const meta = resolveTemplateIndicator(tpl, indicators);
    if (!meta.hasLink || meta.target == null) continue;
    const realized = exec.indicator_realized_value as number;
    pcts.push(computeAchievementPct(realized, meta.target, meta.direction));
    lastMeta = meta;
  }

  if (pcts.length === 0 || !lastMeta) return null;

  const avgPct = Math.round(pcts.reduce((s, p) => s + p, 0) / pcts.length);
  const avgRealized =
    inPeriod.reduce((s, e) => s + (e.indicator_realized_value ?? 0), 0) / inPeriod.length;

  return {
    achievementPct: avgPct,
    realized: Math.round(avgRealized * 100) / 100,
    target: lastMeta.target,
    unit: lastMeta.unit,
    direction: lastMeta.direction,
    source: 'execution',
    detail: `Média de ${pcts.length} valor(es) informado(s) na execução do gemba.`,
  };
}

/** Rótulo agregado dos indicadores vinculados nas rotinas do usuário. */
export function resolveLinkedIndicatorLabel(
  templates: LstTemplate[],
  indicators: Indicator[],
): string | null {
  const linked = templates.filter(templateHasLinkedIndicator);
  if (linked.length === 0) return null;
  if (linked.length === 1) {
    return templateIndicatorLabel(linked[0], indicators);
  }
  return `${linked.length} indicadores`;
}

const UNAVAILABLE: EfficiencyMatrixResult = {
  adherencePct: 0,
  resultPct: null,
  resultLabel: 'N/A',
  hasLinkedIndicator: false,
  resultAvailable: false,
  quadrant: 'indisponivel',
  title: 'Sem Indicador Vinculado',
  message:
    'Vincule um indicador nas configurações para medir o impacto desta rotina (ex.: OEE, Refugo, Setup).',
  tone: 'slate',
  indicatorName: null,
  resultScore: null,
};

export function computeEfficiencyMatrix(
  adherencePct: number,
  linkedIndicators: Indicator[],
  resultScore: IndicatorResultScore | null,
  templateLinkLabel?: string | null,
): EfficiencyMatrixResult {
  const base = { ...UNAVAILABLE, adherencePct };

  const hasTemplateLink = !!templateLinkLabel;
  if (linkedIndicators.length === 0 && !hasTemplateLink) {
    return base;
  }

  const indicatorName =
    templateLinkLabel ??
    (linkedIndicators.length === 1
      ? linkedIndicators[0].name
      : `${linkedIndicators.length} indicadores`);

  if (!resultScore || resultScore.achievementPct == null) {
    return {
      ...base,
      hasLinkedIndicator: true,
      indicatorName,
      resultScore,
      title: 'Meta Indisponível',
      message:
        resultScore?.detail ??
        'Informe o valor realizado ao concluir rotinas com indicador vinculado, ou configure a meta na rotina.',
    };
  }

  const resultPct = resultScore.achievementPct;
  const highAdh = adherencePct >= THRESHOLD;
  const highRes = resultPct >= THRESHOLD;

  let quadrant: EfficiencyQuadrant;
  let title: string;
  let message: string;
  let tone: EfficiencyMatrixResult['tone'];

  if (highAdh && highRes) {
    quadrant = 'eficaz';
    title = 'Processo Excelente';
    message = 'Padrão validado — alta aderência à rotina com atingimento sólido da meta industrial/comercial.';
    tone = 'emerald';
  } else if (highAdh && !highRes) {
    quadrant = 'padrao_ineficaz';
    title = 'Padrão Ineficaz';
    message =
      'Revisar conteúdo das tarefas — alta aderência disciplinar, porém baixo atingimento da meta vinculada.';
    tone = 'amber';
  } else if (!highAdh && highRes) {
    quadrant = 'processo_instavel';
    title = 'Processo Instável';
    message =
      'Resultado por sorte/variabilidade — meta atingida apesar de baixa aderência ao padrão. Reforçar execução no gemba.';
    tone = 'amber';
  } else {
    quadrant = 'critico';
    title = 'Situação Crítica';
    message = 'Baixa aderência e baixo atingimento da meta. Intervenção imediata na reunião de camadas.';
    tone = 'red';
  }

  return {
    adherencePct,
    resultPct,
    resultLabel: `${resultPct}%`,
    hasLinkedIndicator: true,
    resultAvailable: true,
    quadrant,
    title,
    message,
    tone,
    indicatorName,
    resultScore,
  };
}
